import type { DrawdownStatus } from '@paid-ui/enums/drawdown';
import type { ContractSummary } from '@paid-ui/queries/project-summary';
import type { ProjectSummary } from '@paid-ui/queries/search-projects';
import type { Address } from '@paid-ui/schemas/zod/address';
import type { DraftContract } from '@paid-ui/types/contracts';
import type { RequiringAction } from '@paid-ui/types/requiring-actions';

import dayjs from 'dayjs';
import { groupBy } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { proxy } from 'valtio';
import { devtools, subscribeKey } from 'valtio/utils';

import { ClaimMethod } from '@paid-ui/enums/claim';
import {
  ContractRole,
  ContractSource,
  ContractState,
  ContractTemplate,
  ContractType,
} from '@paid-ui/enums/contract';
import { ProjectIndustry, ProjectStatus } from '@paid-ui/enums/project';
import { UserGroupType } from '@paid-ui/enums/user';
import { WorkItemType } from '@paid-ui/enums/work-item';
import { formatProjectName, splitAddress } from '@paid-ui/utils/project';
import { resetProxy } from '@paid-ui/utils/reset-proxy';

import { requiringActionManager } from './requiring-action';
import { userManager } from './user';
import { userGroupManager } from './user-group';

export interface DrawdownItem {
  authorisedAt: string;
  status?: DrawdownStatus;
  budget: number | '';
  actual: number | '';
  costToComplete: number | '';
  available: number | '';
  equity: number | '';
  senior: number | '';
  [key: string]: number | string | undefined;
}

export interface LoadAdjustmentItem {
  id: string;
  status: DrawdownStatus;
  description: string;
  category: string;
  timeExtension?: number;
  amount: number;
  assessed: number;
}
export interface LoanAdjustment {
  items?: LoadAdjustmentItem[];
  updatedAt: number;
}

export interface FundingWaterfall {
  key: string;
  label: string;
  value: number | '';
  color: string;
}

export interface DraftDrawdown {
  name: string;
  version: number;
  items: DrawdownItem[];
  createdAt: number;
  updatedAt: number;
}

export interface ProjectBudget extends DraftDrawdown {
  fundings: FundingWaterfall[];
}

const initialState = {
  id: '',
  createdAt: '',
  projectName: '',
  projectNumber: '',
  projectAddress: null as Address | null,
  projectStatus: null as ProjectStatus | null,
  projectIndustry: ProjectIndustry.CONSTRUCTION,
  customName: '',
  fullProjectName: '',
  suburbAndState: '',
  timezone: '',
  defaultContractId: '',

  primeContract: null as ContractSummary | null,
  contractSummaries: [] as ContractSummary[],
  draftContracts: [] as DraftContract[],
  projectContractSet: new Set<string>(),

  budgetVersion: 0,

  requiringActions: [] as RequiringAction[],
  userGroup: {
    id: '',
  },

  additionalData: {
    projectBudget: [] as ProjectBudget[] | undefined,
    loanAdjustment: {
      items: [],
      updatedAt: 0,
    } as LoanAdjustment | undefined,
  },

  permissions: {
    canEditProject: false,
    canDeleteProject: false,
    canArchiveProject: false,
    canUnarchiveProject: false,
    canCreateContract: false,
    canCreatePrimeContract: false,
    canCreateSuperContract: false,
    canCreateSecondaryContract: false,
    canCreateServiceContract: false,
    canCreateFundingContract: false,
  },

  forceUpdate: 0,
  loading: true,
};

export const projectManager = proxy(initialState);

devtools(projectManager, {
  name: 'Current Project',
  enabled: false,
});

const getDefaultContract = (contracts: ContractSummary[] = []) => {
  const searchParams = new URLSearchParams(globalThis.location.search);
  const contractId = searchParams.get('contractId');
  const contract = contracts.find((item) => item.id === contractId);
  if (contract) return contract;

  const {
    FUNDING_CONTRACT: fundingContracts = [],
    HEAD_CONTRACT: headContracts = [],
    SUBCONTRACT: subcontracts = [],
    SUPPLY_CONTRACT: supplyContracts = [],
    SERVICE_CONTRACT: serviceContracts = [],
    DEED: deeds = [],
  } = groupBy(contracts, (contract) => contract.contractType);

  const [fundingContract] = fundingContracts;
  if (fundingContract) return fundingContract;

  const [headContract] = headContracts;
  if (headContract) return headContract;

  const [subcontract] = subcontracts;
  if (subcontract) return subcontract;

  const [supplyContract] = supplyContracts;
  if (supplyContract) return supplyContract;

  const [serviceContract] = serviceContracts;
  if (serviceContract) return serviceContract;

  const [deed] = deeds;
  if (deed) return deed;

  return null;
};

export const resetProject = () => {
  resetProxy(projectManager, initialState);
};

// Use when navigate from other pages
export const saveProjectSummary = (data: ProjectSummary) => {
  const { id } = projectManager;
  if (id === data.id) return;

  // The search endpoint carries the address as one formatted string rather than the
  // structured `Address`, so `projectAddress` stays null until the project detail query
  // lands. `suburbAndState` is filled from the string so the header reads in the meantime.
  const { locality } = splitAddress(data.formattedAddress);
  const projectName = formatProjectName(data);

  resetProxy(projectManager, {
    ...initialState,
    id: data.id,
    createdAt: data.createdAt,
    projectName,
    projectNumber: data.projectNumber ?? '',
    customName: data.customName ?? '',
    projectStatus: data.projectStatus,
    suburbAndState: locality,
    fullProjectName: [projectName, locality].filter(Boolean).join(' · '),
    defaultContractId: '',
  });
};

// Use when open URL directly
export const saveProject = (data: Partial<typeof initialState>) => {
  const { displayName, disableContractActions } = userManager;
  const { projectActions } = requiringActionManager;
  const {
    permission: { canManageGenericContract, canManagePrimeContract, canManageSecondaryContract },
  } = userGroupManager;

  const {
    projectAddress,
    projectStatus = ProjectStatus.ACTIVE,
    customName = '',
    projectName: _projectName,
    projectNumber = '',
    contractSummaries = [],
    draftContracts = [],
    userGroup,
    additionalData,
  } = data ?? {};

  const { suburb, state } = projectAddress || {};
  const suburbAndState = [suburb, state].filter(Boolean).join(' ');
  const projectName = [projectNumber, customName || _projectName].filter(Boolean).join(' · ');
  const fullProjectName = [projectName, suburbAndState].filter(Boolean).join(' · ');

  const draftSummaries = draftContracts.map((draft) => {
    const { contractType, contract } = draft;
    const progressPayments = contract.progressPayments ?? [];

    return {
      id: draft.draftId,
      name: contract.name,
      source:
        contract.contractTemplate === ContractTemplate.OFF_PLATFORM
          ? ContractSource.OFF_PLATFORM
          : ContractSource.ON_PLATFORM,
      contractTemplate: contract.contractTemplate,
      contractType,
      tags: contract.tags ?? [],
      paymentStructure: contract.paymentSchedule ?? ClaimMethod.STAGED,
      contractPrice: contract.price?.current ?? 0,
      escrowedToDateAmount: 0,
      claimedToDateAmount: contract.price?.paid?.amount ?? 0,
      paidToDateAmount: contract.price?.paid?.amount ?? 0,
      remainingAmount: contract.price?.remaining?.amount ?? 0,
      contractClass: contract.contractClass,
      contractDates: {
        anticipatedCommencementDate: contract.anticipatedCommencementDate,
        commencementDate: contract.commencementDate,
        contractualCompletionDate: contract.contractualCompletionDate,
        completionDate: contract.actualCompletionDate,
      },
      participantParties:
        contract.participantParties?.map((party) => ({
          id: party.id,
          displayName: party.displayName,
          invitationType: party.invitationType,
          linkingStatus: party.linkingStatus,
          mainRoleCategory:
            party.role?.role ??
            (party.isCurrentParty ? ContractRole.BUILDER : ContractRole.CONTRACTOR),
          payType: party.payType,
          roleOnContract:
            party.roleOnContract ?? (party.isCurrentParty ? 'Builder' : 'Subcontractor'),
          type: party.userGroup?.type ?? UserGroupType.BUSINESS,
          isCurrentParty: party.isCurrentParty,
          businessId: party.userGroup?.business?.id || null,
          nominee: {
            id: party.nominee?.id ?? '',
            firstName: party.nominee?.firstName ?? '',
            lastName: party.nominee?.lastName ?? '',
            email: party.nominee?.email ?? '',
            mobile: party.nominee?.mobile ?? '',
            homeAddress: party.nominee?.homeAddress,
            user: {
              id: party.nominee?.user?.id ?? '',
              firstName: party.nominee?.user?.firstName ?? '',
              lastName: party.nominee?.user?.lastName ?? '',
            },
          },
        })) ?? [],
      partyName: displayName,
      theOtherPartyName: contract.theOtherPartyName?.trim() ?? '',
      theOtherPartyEmail: contract.theOtherPartyEmail ?? '',
      numberOfParticipants: contract.participantParties?.length ?? 0,
      progressPayments: progressPayments.map((payment) => ({
        id: payment.id ?? uuidv4(),
        customName: payment.customName ?? '',
        description: payment.description,
        percentOfContractSum: payment.percentOfContractSum ?? 0,
        taxableAmount: {
          amount: payment.amount ?? 0,
          excludeGstAmount: payment.amount ?? 0,
          gstAmount: 0,
        },
      })),
      numberOfPayments: progressPayments.length,
      lineWorks: contract.lineWorkItems?.map((item) => ({
        id: item.id,
        code: item.code ?? '',
        description: item.description ?? '',
        type: WorkItemType.LINE,
        workItemStatus: item.workItemStatus,
        taxableAmount: {
          amount: item.amount ?? 0,
          excludeGstAmount: item.amount ?? 0,
          gstAmount: 0,
        },
      })),
      variationWorks: contract.variationWorkItems?.map((item) => ({
        id: item.id,
        code: item.code ?? '',
        description: item.description ?? '',
        type: WorkItemType.VARIATION,
        workItemStatus: item.workItemStatus,
        taxableAmount: {
          amount: item.amount ?? 0,
          excludeGstAmount: item.amount ?? 0,
          gstAmount: 0,
        },
      })),
      numberOfWorkItems: contract.lineWorkItems?.length ?? 0,
      paymentSchedule: contract.paymentSchedule,
      paymentTerms: contract.paymentTerms,
      attachments: contract.attachments ?? [],
      contractState: contract.contractState,
      isDraftContract: true,
      createdAt: contract.updatedAt ? dayjs(contract.updatedAt).toISOString() : undefined,
      contract,
    } as ContractSummary;
  });

  const allContractSummaries = [...contractSummaries, ...draftSummaries];
  const defaultContract = getDefaultContract(allContractSummaries);
  const primeContract =
    allContractSummaries.find(
      (contract) =>
        contract.contractType === ContractType.HEAD_CONTRACT &&
        contract.contractState !== ContractState.ABANDONED,
    ) ?? null;

  const canArchiveProject = !disableContractActions && projectStatus === ProjectStatus.ACTIVE;
  const canUnarchiveProject = !disableContractActions && projectStatus === ProjectStatus.ARCHIVED;
  const canEditProject = !disableContractActions && projectStatus === ProjectStatus.ACTIVE;
  const canDeleteProject = !disableContractActions && allContractSummaries.length === 0;
  const canCreateContract = !disableContractActions && projectStatus === ProjectStatus.ACTIVE;

  const permissions = {
    canArchiveProject,
    canUnarchiveProject,
    canEditProject,
    canDeleteProject,
    canCreateContract,
    canCreatePrimeContract: canCreateContract && !primeContract && canManagePrimeContract,
    canCreateSecondaryContract: canCreateContract && canManageSecondaryContract,
    canCreateServiceContract: canCreateContract && canManageGenericContract,
    canCreateFundingContract: canCreateContract && canManageGenericContract,
  };

  resetProxy(projectManager, {
    ...data,
    customName,
    projectNumber,
    projectName,
    projectStatus,
    projectIndustry: ProjectIndustry.CONSTRUCTION,
    suburbAndState,
    fullProjectName,
    contractSummaries: allContractSummaries,
    draftContracts,
    primeContract,
    userGroup,
    projectContractSet: new Set(allContractSummaries.map((contract) => contract.id)),
    defaultContractId: defaultContract?.id ?? '',
    permissions,
    requiringActions: data.id ? (projectActions[data.id] ?? []) : [],
    additionalData: additionalData ?? {
      projectBudget: [],
    },
    loading: false,
  });
};

export const setBudgetVersion = (version: string | number) => {
  projectManager.budgetVersion = Number(version);
};

export const reloadProject = () => {
  const { id, forceUpdate } = projectManager;
  if (!id) return;
  projectManager.loading = true;
  projectManager.forceUpdate = forceUpdate > 100 ? 1 : forceUpdate + 1;
};

subscribeKey(requiringActionManager, 'projectActions', (value) => {
  const { id } = projectManager;
  if (!id) return;
  projectManager.requiringActions = value[id] ?? [];
});
